import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, ArrowUpRight, Instagram } from "lucide-react";
import { CONTACTS, EMAIL, WHATSAPP_URL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/constants";

export default function Contact() {
  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 divider-glow" />
      <div className="absolute -bottom-32 left-0 h-[420px] w-[420px] rounded-full bg-[#0055ff] opacity-[0.1] blur-[140px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-blue-400" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-blue-300">
                Contatti
              </span>
            </div>
            <h2
              data-testid="contact-title"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-[-0.02em] leading-[1.02] text-white"
            >
              Parla{" "}
              <span className="metal-text-gradient">direttamente</span> con
              noi.
            </h2>
            <p className="mt-6 text-gray-400 text-base md:text-lg leading-relaxed max-w-md">
              Richiedi il tuo preventivo o ricevi assistenza personalizzata in
              tempo reale. Risposta rapida via WhatsApp.
            </p>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta-whatsapp"
              className="shimmer-btn mt-10 inline-flex items-center gap-3 px-8 py-5 bg-[#0055ff] text-white font-heading font-bold uppercase tracking-[0.15em] text-sm animate-pulse-glow hover:bg-[#0066ff] transition-colors"
            >
              <MessageCircle size={18} strokeWidth={2.5} />
              Richiedi Preventivo
            </a>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta-instagram"
              className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-3 px-8 py-5 text-white font-heading font-bold uppercase tracking-[0.15em] text-sm border border-white/15 hover:border-transparent hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] transition-all duration-500 relative overflow-hidden group"
            >
              <Instagram size={18} strokeWidth={2.5} />
              Seguici su Instagram
            </a>
          </motion.div>

          {/* Right contact cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 space-y-4"
          >
            {CONTACTS.map((c, i) => (
              <div
                key={c.tel}
                className="group relative overflow-hidden border border-white/10 bg-[#0A0D14] hover:border-blue-500/50 transition-colors p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between"
                data-testid={`contact-person-${i}`}
              >
                <div className="flex items-center gap-5">
                  <div className="flex h-14 w-14 items-center justify-center bg-blue-600/15 border border-blue-500/30">
                    <Phone size={22} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80">
                      Referente
                    </div>
                    <div className="font-heading text-2xl font-bold text-white uppercase tracking-tight mt-1">
                      {c.name}
                    </div>
                    <a
                      href={`tel:${c.tel}`}
                      className="text-gray-300 hover:text-white transition-colors mt-1 inline-block text-sm tracking-wide"
                      data-testid={`contact-phone-${i}`}
                    >
                      {c.phone}
                    </a>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={`tel:${c.tel}`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/15 hover:border-blue-400 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-white/5 transition-all"
                    data-testid={`contact-call-${i}`}
                  >
                    <Phone size={14} />
                    Chiama
                  </a>
                  <a
                    href={c.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-500 text-white text-xs font-bold uppercase tracking-[0.16em] transition-all"
                    data-testid={`contact-whatsapp-${i}`}
                  >
                    <MessageCircle size={14} />
                    WhatsApp
                  </a>
                </div>
              </div>
            ))}

            {/* Email card */}
            <div className="relative overflow-hidden border border-white/10 bg-[#0A0D14] hover:border-blue-500/50 transition-colors p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
              <div className="flex items-center gap-5">
                <div className="flex h-14 w-14 items-center justify-center bg-blue-600/15 border border-blue-500/30">
                  <Mail size={22} className="text-blue-400" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80">
                    Email Certificata
                  </div>
                  <a
                    href={`mailto:${EMAIL}`}
                    className="font-heading text-lg md:text-xl font-bold text-white hover:text-blue-300 transition-colors mt-1 block break-all"
                    data-testid="contact-email"
                  >
                    {EMAIL}
                  </a>
                </div>
              </div>
              <a
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/15 hover:border-blue-400 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-white/5 transition-all self-start sm:self-auto"
                data-testid="contact-email-cta"
              >
                Invia mail
                <ArrowUpRight size={14} />
              </a>
            </div>

            {/* Instagram card */}
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-card"
              className="group relative overflow-hidden border border-white/10 hover:border-transparent bg-[#0A0D14] transition-all p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between"
            >
              <span className="absolute inset-0 bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] opacity-0 group-hover:opacity-15 transition-opacity duration-500" />
              <div className="relative flex items-center gap-5">
                <div className="flex h-14 w-14 items-center justify-center bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] shadow-[0_0_20px_rgba(220,39,67,0.4)]">
                  <Instagram size={22} className="text-white" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-pink-300/80">
                    Seguici su Instagram
                  </div>
                  <div className="font-heading text-lg md:text-xl font-bold text-white mt-1 group-hover:text-white transition-colors">
                    {INSTAGRAM_HANDLE}
                  </div>
                </div>
              </div>
              <div className="relative inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs font-bold uppercase tracking-[0.16em] text-white self-start sm:self-auto group-hover:border-white transition-colors">
                Apri Instagram
                <ArrowUpRight size={14} />
              </div>
            </a>

            {/* Coverage card */}
            <div className="relative overflow-hidden border border-blue-500/30 bg-gradient-to-r from-[#0A0D14] to-[#0a1230] p-6 md:p-8 flex items-center gap-5">
              <div className="flex h-14 w-14 items-center justify-center bg-blue-600/25 border border-blue-400/50">
                <MapPin size={22} className="text-blue-300" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80">
                  Sede · Canicattì (AG) · Sicilia
                </div>
                <div className="font-heading text-lg md:text-xl font-bold text-white mt-1 uppercase">
                  Copertura in tutta Italia · Trasporti su strada
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
